import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableSortLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import CommonSearchCriteria from "../../common/CommonSearchCriteria";
import {
  DeleteUserRoute,
  DuplicateUserRoute,
  MarkCompletedUserRoute, GetUserRoutesByCriteria, PrintUserRouteDoc
} from "../../../services/UserRoutesService";
import UserService from "../../../../globel-constants/UserService";
import ViewUserRoute from "./ViewUserRoute";
import UserRouteMap from "./UserRouteMap";
import ClientCoCPrintPopup from "./ClientCoCPrintPopup";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { userRouteStatusList } from "../../common/statusLists";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";

const UserRoutesAdminTable = () => {
  const columns = [
    { id: "id",           label: "Route Id"  },
    { id: "routeName",    label: "Route Name" },
    { id: "userId",       label: "User"       },
    { id: "routeDate",    label: "Date"       },
    { id: "routeClients", label: "Clients"    },
    { id: "actions",      label: "Actions"    },
  ];

  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [routesData, setRoutesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [duplicateRouteName, setDuplicateRouteName] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [tableHeight, setTableHeight] = useState(600);

  const tableContainerRef = useRef(null);

  const [searchCriteria, setSearchCriteria] = useState({
    searchText: "",
    orderStatus: "Not Completed",
    startDate: "",
    endDate: "",
    pageNumber: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();

  // Dynamic table height calculation — same approach as OutsourcedAnalysisTable
  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        // Reserve space for pagination (56px) and bottom padding (20px)
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [filteredData]);

  useEffect(() => {
    checkPermission();
    fetchRoutes(searchCriteria);
  }, [isDeleted]);

  useEffect(() => {
    if (!routesData) return;
    const columnHeaders = ["id", "routeName", "userId", "routeDate", "routeClients"];
    const filteredResults = routesData.filter((route) =>
      columnHeaders.some((header) => {
        const value = route[header];
        return value && typeof value === "string"
          ? value.toLowerCase().includes(searchTerm.toLowerCase())
          : false;
      })
    );
    setFilteredData(filteredResults);
  }, [searchTerm, routesData]);

  const checkPermission = () => {
    let user = UserService.getUserInfoFromLocalStorage();
    if (!user?.isReportAdmin) {
      navigate(`/vlp/unauthorized`);
    } else {
      return true;
    }
  };

  const fetchRoutes = async (searchCriteria) => {
    try {
      setLoading(true);
      const response = await GetUserRoutesByCriteria(
        searchCriteria.startDate,
        searchCriteria.endDate,
        searchCriteria.orderStatus,
        searchCriteria.searchText
      );
      setRoutesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching routes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchCriteria = async (changedSearchCriteria) => {
    const updatedSearchCriteria = {
      ...searchCriteria,
      startDate: changedSearchCriteria.startDate,
      endDate: changedSearchCriteria.endDate,
      orderStatus: changedSearchCriteria.orderStatus,
      searchText: changedSearchCriteria.searchText,
      pageNumber: 0,
    };
    setSearchCriteria(updatedSearchCriteria);
    fetchRoutes(updatedSearchCriteria);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBackClick = () => {
    setSelectedRoute(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (route) => {
    setSelectedRoute(route);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedRoute(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteUserRoute(selectedRoute.id);
      if (response.results === true) {
        NotificationManager.success("Route deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setIsDeleted(!isDeleted);
        setSelectedRoute(null);
      } else {
        NotificationManager.error("Error deleting route");
      }
    } catch (error) {
      NotificationManager.error("Error deleting route");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRouteClick = () => navigate("/vlp/user-routes/add");

  const handleEditRouteClick = (route) =>
    window.open(`/vlp/user-routes/${route.id}`, "_blank");

  const handleDuplicateClick = (route) => {
    setDuplicateRouteName(route.routeName);
    setSelectedRoute(route);
    setIsDuplicateDialogOpen(true);
  };

  const handleDuplicateDialogClose = () => {
    setIsDuplicateDialogOpen(false);
    setDuplicateRouteName("");
    setSelectedRoute(null);
  };

  const handlePrintClick = (route) => {
    setSelectedRoute(route);
    setIsPrintDialogOpen(true);
  };

  const handlePrintDialogClose = () => {
    setIsPrintDialogOpen(false);
    setSelectedRoute(null);
  };

  const handleDuplicateConfirm = async () => {
    if (
      !duplicateRouteName.trim() ||
      duplicateRouteName.trim() === selectedRoute.routeName
    ) {
      NotificationManager.error("Please provide a valid route name");
      return;
    }
    try {
      setLoading(true);
      const routeData = { ...selectedRoute, routeName: duplicateRouteName };
      const response = await DuplicateUserRoute(selectedRoute.id, routeData);
      if (response.results === true) {
        NotificationManager.success("Route duplicated successfully");
        setIsDuplicateDialogOpen(false);
        setDuplicateRouteName("");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error duplicating route");
      }
    } catch (error) {
      NotificationManager.error("Error duplicating route");
    } finally {
      setLoading(false);
      setSelectedRoute(null);
    }
  };

  const printRouteClientCoC = (route) => handlePrintClick(route);

  const printRouteDoc = (route) => handleDocumentPrint(route);

  const handleDocumentPrint = async (route) => {
    setLoading(true);
    try {
      await PrintUserRouteDoc(route.id);
      NotificationManager.success("CoCs downloaded successfully");
    } catch (error) {
      NotificationManager.error("Error generating CoC:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const markCompletedRoute = async (route) => {
    try {
      setLoading(true);
      const response = await MarkCompletedUserRoute(route.id);
      if (response.results === true) {
        NotificationManager.success("Route completed successfully");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error completing route");
      }
    } catch (error) {
      NotificationManager.error("Error completing route");
    } finally {
      setLoading(false);
    }
  };

  const renderActions = (route) => (
    <div style={{ minWidth: "120px" }}>
      <Tooltip title="Print Chain of Custody">
        <IconButton style={{ color: "#e7ac17" }} onClick={() => printRouteClientCoC(route)}>
          <LocalPrintshopIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={route.isCompleted ? "Mark as Not Completed" : "Mark as Completed"}>
        <IconButton style={{ color: "#c603fc" }} onClick={() => markCompletedRoute(route)}>
          <DoneAllIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Edit Route">
        <IconButton style={{ color: "orange" }} onClick={() => handleEditRouteClick(route)}>
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete Route">
        <IconButton style={{ color: "red" }} onClick={() => handleDeleteClick(route)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Duplicate Route">
        <IconButton style={{ color: "#03d7fc" }} onClick={() => handleDuplicateClick(route)}>
          <ContentCopyIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Print Pickup Routes Details">
        <IconButton style={{ color: "#2d92eb" }} onClick={() => printRouteDoc(route)}>
          <LocalPrintshopIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">Pickup Routes</span>
      <hr />

      {(selectedRoute && !isPrintDialogOpen) ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewUserRoute route={selectedRoute} />
          <UserRouteMap accounts={selectedRoute.routeClients} isForView={true} />
        </>
      ) : (
        <>
          <div style={{ marginBottom: "20px" }}>
            <CommonSearchCriteria
              orderStatusOptions={userRouteStatusList}
              onSearch={handleSearchCriteria}
              extraActions={
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddRouteClick}
                  style={{ height: "40px", whiteSpace: "nowrap" }}
                >
                  New
                </Button>
              }
              searchTooltip="Search by Route Name, Comments, or Client Account Numbers"
              dateRangeTooltip="Filter routes by their scheduled Route Date"
            />
          </div>

          <Paper style={{ marginRight: "20px" }}>
            <TableContainer
              ref={tableContainerRef}
              sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}
            >
              <Table sx={{ minWidth: 400 }} size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.id === "actions" ? col.label : (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleRequestSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((route, index) => (
                      <TableRow
                        key={route.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {columns.slice(0, -1).map((col) => (
                          <TableCell key={col.id}>
                            {col.id === "id" ? (
                              <span
                                onClick={() => handleEditRouteClick(route)}
                                style={{
                                  color: "#1976d2",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontWeight: 500,
                                }}
                              >
                                {route[col.id]}
                              </span>
                            ) : col.id === "routeDate" && route[col.id] ? (
                              new Date(route[col.id]).toLocaleDateString("en-US")
                            ) : route[col.id] ? (
                              String(route[col.id]).substring(0, 35)
                            ) : (
                              ""
                            )}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(route)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this route?"
        color="red"
      />

      {isPrintDialogOpen && (
        <ClientCoCPrintPopup
          open={isPrintDialogOpen}
          routeClients={selectedRoute.routeClients}
          onClose={handlePrintDialogClose}
        />
      )}

      <Dialog open={isDuplicateDialogOpen} onClose={handleDuplicateDialogClose}>
        <DialogTitle>Duplicate Route</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Route Name"
            type="text"
            fullWidth
            variant="outlined"
            value={duplicateRouteName}
            size="small"
            onChange={(e) => setDuplicateRouteName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDuplicateConfirm} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <CommonLoader loading={loading} />
    </>
  );
};

export default UserRoutesAdminTable;